/**
 * WebSocket push channel `GET /api/v1/ws?token=<accessToken>`.
 *
 * Messages: `{ type: 'notification', data: Notification }` and `{ type: 'event', name, data }`.
 * The connection is started by the admin/ESS shells after login and closed on logout.
 *
 * @example
 *   useRealtimeEvent('bill.invoice.issued', (data) => invalidateApi('/bill/invoices'));
 */
import { useEffect, useRef } from 'react';
import { apiPath } from './api';
import { refreshAccessToken } from './api';
import { tokenStore } from './tokenStore';

export interface NotificationDto {
  id: string;
  type: string;
  severity: 'INFO' | 'SUCCESS' | 'WARNING' | 'CRITICAL';
  titleAr: string;
  titleEn: string;
  bodyAr?: string | null;
  bodyEn?: string | null;
  link?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  readAt?: string | null;
  createdAt: string;
}

export type RealtimeMessage =
  | { type: 'notification'; data: NotificationDto }
  | { type: 'event'; name: string; data: unknown };

type Listener = (msg: RealtimeMessage) => void;

class RealtimeClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private retry = 0;
  private timer: number | null = null;
  private wanted = false;

  start(): void {
    this.wanted = true;
    if (!this.ws) this.connect();
  }

  stop(): void {
    this.wanted = false;
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = null;
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000, 'logout');
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private url(token: string): string {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}${apiPath('/ws')}?token=${encodeURIComponent(token)}`;
  }

  private connect(): void {
    const token = tokenStore.get();
    if (!token || !this.wanted) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url(token));
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
    };
    ws.onmessage = (ev: MessageEvent<string>) => {
      try {
        const msg = JSON.parse(ev.data) as RealtimeMessage;
        if (msg && (msg.type === 'notification' || msg.type === 'event')) this.listeners.forEach((l) => l(msg));
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onclose = (ev) => {
      if (this.ws === ws) this.ws = null;
      if (!this.wanted) return;
      // 4401 / 1008 → token rejected: refresh first
      if (ev.code === 4401 || ev.code === 1008) {
        void refreshAccessToken().then((ok) => (ok ? this.scheduleReconnect() : undefined));
      } else this.scheduleReconnect();
    };
    ws.onerror = () => ws.close();
  }

  private scheduleReconnect(): void {
    if (this.timer || !this.wanted) return;
    const delay = Math.min(30_000, 1000 * 2 ** this.retry++);
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.connect();
    }, delay);
  }
}

export const realtime = new RealtimeClient();

/** Subscribe to every realtime message while the component is mounted. */
export function useRealtime(handler: Listener): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => realtime.subscribe((m) => ref.current(m)), []);
}

/** Subscribe to one named domain event (`{ type: 'event', name }`). */
export function useRealtimeEvent<T = unknown>(name: string, handler: (data: T) => void): void {
  useRealtime((m) => {
    if (m.type === 'event' && m.name === name) handler(m.data as T);
  });
}
